import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import { BiMessageSquareX, BiUpload } from 'react-icons/bi'
import { FormattedMessage, IntlProvider } from 'react-intl'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import SignatureCanvas from 'react-signature-canvas'
import {
  Box,
  Center,
  FormControl,
  Heading,
  Icon,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react'
import { Text2Image } from '@xg4/text2image'
import { format } from 'date-fns'
import { times } from 'lodash'
import { useRecoilState, useRecoilValue } from 'recoil'

import { BasicField, FormFieldDto } from '~shared/types/field'
import {
  FormAuthType,
  FormColorTheme,
  LogicDto,
  PaymentCharges,
} from '~shared/types/form'

import { BxsErrorCircle } from '~assets/icons'
import DefaultUploadImage from '~assets/images/sig-upload.png'
import { ThemeColorScheme } from '~theme/foundations/colours'
import { useToast } from '~hooks/useToast'
import { ApiService } from '~services/ApiService'
import Button from '~components/Button'
import DateInput from '~components/DatePicker'
import { SingleSelect } from '~components/Dropdown'
import FormErrorMessage from '~components/FormControl/FormErrorMessage'
import FormLabel from '~components/FormControl/FormLabel'
import IconButton from '~components/IconButton'
import { FormFieldValues } from '~templates/Field'
import { createTableRow } from '~templates/Field/Table/utils/createRow'

import {
  validateAgencyLogoFormat,
  validateImageSize,
} from '~pages/Agency/utils'
import { augmentWithMyInfo } from '~features/myinfo/utils/augmentWithMyInfo'
import {
  SubmitStorageFormArgsDraft,
  submitStorageModeFormDrafts,
} from '~features/public-form/PublicFormService'

import {
  DraftSubmissionId,
  LanguageFilter,
  SignatureRequiredMsge,
  SignatureTypeRequiredMsge,
} from '../../../../../atom'
import Input from '../../../../components/Input/index'

import { CancelModal } from './CancelModal'
import { PublicFormSubmitButton } from './PublicFormSubmitButton'
import { VisibleFormFields } from './VisibleFormFields'

export interface FormFieldsProps {
  formFields: FormFieldDto[]
  formLogics: LogicDto[]
  colorTheme: FormColorTheme
  paymentRequired?: boolean
  preview?: boolean
  paymentAmount?: number
  paymentCharges?: PaymentCharges[]
  formAuthType?: FormAuthType
  signatureRequired?: boolean
  signatureTypes?: any
  onSubmit: SubmitHandler<FormFieldValues>
}

export const FormFields = ({
  formFields,
  formLogics,
  colorTheme,
  paymentRequired,
  preview,
  paymentAmount,
  paymentCharges,
  formAuthType,
  signatureRequired,
  signatureTypes,
  onSubmit,
}: FormFieldsProps): JSX.Element => {
  const [disabled, setDisabled] = useState(false)
  const navigate = useNavigate()
  const { formId } = useParams()
  const toast = useToast()
  const [draftId, setDraftId] = useRecoilState(DraftSubmissionId)
  const signatureErrorMsge = useRecoilValue(SignatureRequiredMsge)
  const signatureTypeErrorMsge = useRecoilValue(SignatureTypeRequiredMsge)
  const [draftContent, setDraftContent] = useState<any[]>()
  const draftButtonRef = useRef<HTMLButtonElement | null>(null)
  const MINUTE_MS = 60000
  const [showFields, setShowFields] = useState<boolean>(false)
  const [cancelModal, setCancelModal] = useState<boolean>(false)
  const [signatureType, setSignatureType] = useState<string>('')
  const [signatureTypesList, setSignatureTypesList] = useState<any>()

  const [initialDraftId, setInitialDraft] = useState(draftId)
  const [signatureImage, setSignatureImage] = useState<string>('')
  const [signatureImageFile, setSignatureImageFile] = useState<File | null>(
    null,
  )
  const [signatureName, setSignatureName] = useState<string>('')
  const locale: any = useRecoilValue(LanguageFilter)
  const location = useLocation()
  const tempLanguage = location?.state?.tempLanguage
  const [language, setSelectedLanguage] = useRecoilState(LanguageFilter)
  // useEffect(
  //   () => () => {
  //     setDraftId(null)
  //   },
  //   [],
  // )
  const signPad = useRef<any>(null)
  const signatureImageRef = useRef<HTMLInputElement | null>(null)

  const convertBase64 = (file: File): Promise<string | ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)

      fileReader.onload = () => {
        if (fileReader.result === null) {
          throw new Error()
        }
        resolve(fileReader.result)
      }
      fileReader.onerror = (error) => {
        reject(error)
      }
    })
  }

  const validateSignatureImage = async (file: File): Promise<boolean> => {
    const isImageValid = validateImageSize(file, 2000000)

    if (isImageValid === false) {
      toast({
        description: `${
          locale === 'en'
            ? 'Selected file for the signature has to be less than 2MB'
            : locale === 'si'
            ? 'අත්සන සඳහා තෝරාගත් ගොනුව 2MB ට වඩා අඩු විය යුතුය'
            : 'கையொப்பத்திற்கான தேர்ந்தெடுக்கப்பட்ட கோப்பு 2MB க்கும் குறைவாக இருக்க வேண்டும்'
        }`,
        status: 'danger',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
      return false
    }
    const isImageTypeValid = validateAgencyLogoFormat(file?.name)

    if (!isImageTypeValid) {
      toast({
        description: `${
          locale === 'en'
            ? 'Invalid signature image type. Allowed types: png, jpg, jpeg'
            : locale === 'si'
            ? 'වලංගු නොවන අත්සන් රූප වර්ගය. අවසර ලත් වර්ග: png, jpg, jpeg'
            : 'தவறான கையொப்ப பட வகை. அனுமதிக்கப்பட்ட வகைகள்: png, jpg, jpeg'
        }`,

        duration: 5000,
        isClosable: true,
        status: 'danger',
        position: 'top-right',
      })
      return false
    }

    const encodedAttachmentFiles = await convertBase64(file)

    if (typeof encodedAttachmentFiles !== 'string') {
      toast({
        description: `${
          locale === 'en'
            ? 'Please upload signature again'
            : locale === 'si'
            ? 'කරුණාකර අත්සන නැවත උඩුගත කරන්න'
            : 'கையொப்பத்தை மீண்டும் பதிவேற்றவும்'
        }`,
        status: 'danger',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      })
      return false
    }
    // const encodedAttachment = encodedAttachmentFiles.split(',')[1]

    mainFormValues.setValue('signature', encodedAttachmentFiles)
    localStorage.setItem('signature', encodedAttachmentFiles)
    return true
  }
  useEffect(
    () => () => {
      window.history.replaceState({}, '')
    },
    [],
  )

  useEffect(() => {
    if (tempLanguage) {
      setSelectedLanguage(tempLanguage)
    }
  }, [locale])

  useEffect(() => {
    if (signatureImageFile !== null) {
      setSignatureImage(URL.createObjectURL(new Blob([signatureImageFile])))
    }
  }, [signatureImageFile])

  useEffect(() => {
    if (signatureErrorMsge !== null) {
      mainFormValues.setError('signature', { message: signatureErrorMsge })
    } else {
      mainFormValues.clearErrors('signature')
    }
    if (signatureTypeErrorMsge !== null) {
      mainFormValues.setError('type', { message: signatureTypeErrorMsge })
    } else {
      mainFormValues.clearErrors('type')
    }
  }, [signatureErrorMsge, signatureTypeErrorMsge])

  const removeImage = () => {
    setSignatureImage('')
    setSignatureImageFile(null)
    mainFormValues.setValue('signature', '')
    localStorage.setItem('signature', '')
    localStorage.setItem('signatureType', '')
  }

  const mainFormValues = useForm({
    defaultValues: {
      type: '',
      signature: '',
      date: format(new Date(), 'dd/MMM/yyyy'),
    },
  })
  //if draft is there.. load the draft
  useEffect(() => {
    if (initialDraftId && initialDraftId !== null) {
      ApiService.get(
        `/public/submission/${formId}/submissions/${initialDraftId}/encrypt/draft`,
      )
        .then((data: any) => {
          if (data.status === 200) {
            const actualContentBuffer = Buffer.from(
              data.data?.content,
              'base64',
            )
            const decodedContent = actualContentBuffer.toString('utf8')
            const decodedContentAsJson = JSON.parse(decodedContent)
            console.log('decodedContentAsJson', decodedContentAsJson)
            setDraftContent(decodedContentAsJson)
          }
        })
        .catch((error) => {
          console.log('error', error)
        })
    }
  }, [initialDraftId])

  useEffect(() => {
    const interval = setInterval(() => {
      if (formAuthType && formAuthType !== FormAuthType.NIL) {
        draftButtonRef.current?.click()
      }
    }, MINUTE_MS)

    return () => clearInterval(interval) // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, [])

  useEffect(() => {
    if (signatureRequired) {
      localStorage.removeItem('signatureType')
      localStorage.removeItem('signature')
      localStorage.setItem('signatureRequired', 'true')
    } else {
      localStorage.setItem('signatureRequired', 'false')
    }

    const signTypes: any = []
    signatureTypes?.map((value: any, index: any) => {
      if (value == 'FullName') {
        signTypes.push({
          value: value,
          label: `${
            locale === 'en'
              ? 'Type Full Name for the Signature'
              : locale === 'si'
              ? 'අත්සන සඳහා සම්පූර්ණ නම ටයිප් කරන්න'
              : 'கையொப்பத்திற்கான முழுப் பெயரை உள்ளிடவும்'
          }`,
        })
      } else if (value == 'Touchpad') {
        signTypes.push({
          value: value,
          label: `${
            locale === 'en'
              ? 'Sign by using a touch screen or mouse'
              : locale === 'si'
              ? 'ස්පර්ශ තිරයක් හෝ මූසිකයක් භාවිතයෙන් අත්සන් කරන්න'
              : 'தொடுதிரை அல்லது சுட்டியைப் பயன்படுத்தி கையொப்பமிடுங்கள்'
          }`,
        })
      } else {
        signTypes.push({
          value: value,
          label: `${
            locale === 'en'
              ? 'Insert an image with Signature'
              : locale === 'si'
              ? 'අත්සන සහිත රූපයක් ඇතුළු කරන්න'
              : 'கையொப்பத்துடன் படத்தைச் செருகவும்'
          }`,
        })
      }
    })
    setSignatureTypesList(signTypes)
  }, [])

  // TODO: Inject default values if field is also prefilled.
  const augmentedFormFields = useMemo(
    () => formFields.map(augmentWithMyInfo),
    [formFields],
  )

  const defaultFormValues = useMemo(() => {
    let count = 0
    return augmentedFormFields.reduce<FormFieldValues>((acc, field, index) => {
      if (
        field.fieldType === BasicField.Statement ||
        field.fieldType === BasicField.Image
      ) {
        count += 1
        return acc
      }
      // if (field.fieldValue !== undefined) {
      //   acc[field._id] = field.fieldValue
      //   return acc
      // }

      if (draftContent) {
        if (draftContent[count]?._id === field._id) {
          if (field.fieldType === BasicField.Table) {
            const mainArray = []
            for (let i = 0; i < draftContent[count].answerArray.length; i++) {
              let sampleObject = {}
              for (let j = 0; j < field.columns.length; j++) {
                sampleObject = {
                  ...sampleObject,
                  [field.columns[j]._id]: draftContent[count].answerArray[i][j],
                }
              }
              mainArray.push(sampleObject)
            }
            acc[field._id] = mainArray
            count += 1
            return acc
          }

          acc[field._id] = draftContent[count]?.answer
            ? draftContent[count].answer
            : draftContent[count]?.answerArray
            ? draftContent[count].answerArray
            : ''
          count += 1
          return acc
        }
      }

      switch (field.fieldType) {
        // Required so table column fields will render due to useFieldArray usage.
        // See https://react-hook-form.com/api/usefieldarray
        case BasicField.Table:
          acc[field._id] = times(field.minimumRows, () => createTableRow(field))
          break
      }
      return acc
    }, {})
  }, [augmentedFormFields, draftContent])

  const formMethods = useForm<FormFieldValues>({
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    shouldUnregister: true,
  })

  const watchAllFields = formMethods.watch()

  useEffect(() => {
    formMethods.reset(defaultFormValues)
    if (initialDraftId) {
      if (!draftContent) {
        return
      }
      if (Object.keys(defaultFormValues).length !== 0) {
        setShowFields(true)
      }
    } else {
      setShowFields(true)
    }
  }, [defaultFormValues, draftContent, initialDraftId])

  const handleOnEnd = () => {
    if (signPad.current) {
      console.log(signPad.current.toDataURL())
      localStorage.setItem('signature', signPad.current.toDataURL())
    }
  }

  useEffect(() => {
    const sign = localStorage.getItem('signature')
    if (signPad?.current && sign) {
      signPad.current.fromDataURL(sign)
    }
  }, [disabled])

  useEffect(() => {
    if (signatureName) {
      // const ti = new Text2Image()
      // const url = ti.toDataURL(signatureName)

      // Create a temporary canvas element
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      if (tempCtx !== null) {
        // Set font to measure text dimensions
        tempCtx.font = '16px Arial' // Adjust font size and family as needed

        // Measure text dimensions
        const textWidth = tempCtx.measureText(signatureName).width
        const textHeight = 16 // Assuming font size is 16px

        // Create a canvas element with adjusted size
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = textWidth + 20 // Add some padding for better visualization
        canvas.height = textHeight + 20 // Add some padding for better visualization

        if (ctx !== null) {
          // Set font and fill text
          ctx.font = '16px Arial' // Adjust font size and family as needed
          ctx.fillStyle = 'black'
          ctx.fillText(signatureName, 10, 20) // Adjust position as needed

          // Convert canvas content to a blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Convert blob to base64
              const reader = new FileReader()
              reader.readAsDataURL(blob)
              reader.onloadend = () => {
                const base64data = reader.result
                console.log(base64data)
                localStorage.setItem(
                  'signature',
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  base64data !== null ? base64data : '',
                )
              }
            } else {
              console.log('Error converting canvas to blob')
            }
          }, 'image/png')
        }
      }
    } else {
      localStorage.removeItem('signature')
    }
  }, [signatureName])

  const saveSubmissionAsDraft = useCallback(async () => {
    try {
      if (formMethods.formState.isDirty) {
        if (formId) {
          console.log('watchAllFields', watchAllFields)
          const storageFormArgs: SubmitStorageFormArgsDraft = {
            formFields: formFields,
            formInputs: watchAllFields,
            formId: formId,
            draftId: draftId === null ? undefined : draftId,
          }
          // formMethods.trigger()
          const result = await submitStorageModeFormDrafts(storageFormArgs)
          console.log('result', result.submissionId)
          const subId: any = result?.submissionId
          setDraftId(subId)
        } else {
          console.log('Cannot find form ID.')
        }
      }
    } catch (e) {
      console.log(e)
    }
  }, [
    formMethods.formState.isDirty,
    draftId,
    formFields,
    formId,
    setDraftId,
    watchAllFields,
  ])

  const css = `
    div.is-disabled
      {
        pointer-events: none;
        opacity: 0.7;
      }
    `

  return (
    <IntlProvider locale={locale}>
      <FormProvider {...formMethods}>
        {cancelModal ? (
          <CancelModal
            isOpen={cancelModal}
            onClose={() => {
              setCancelModal(false)
            }}
            onYesClick={async () => {
              await saveSubmissionAsDraft()
              setDraftId(null)
              navigate('/citizen/dashboard', {
                state: { tempLanguage: locale },
              })
            }}
            onNoClick={() => {
              setCancelModal(false)
              setDraftId(null)
              navigate('/citizen/dashboard', {
                state: { tempLanguage: locale },
              })
            }}
          />
        ) : null}
        <style>{css}</style>
        <form onSubmit={formMethods.handleSubmit(onSubmit)} noValidate>
          {/* {!!formFields?.length && <Box></Box>} */}
          {!!formFields?.length && (
            <div>
              <Box bg="white" py="2.5rem" px={{ base: '1rem', md: '2.5rem' }}>
                <Stack spacing="2.25rem">
                  <div
                    aria-disabled={disabled}
                    className={disabled ? 'is-disabled' : ''}
                  >
                    {showFields ? (
                      <VisibleFormFields
                        colorTheme={colorTheme}
                        control={formMethods.control}
                        formFields={augmentedFormFields}
                        formLogics={formLogics}
                        defaultValues={defaultFormValues}
                      />
                    ) : null}
                  </div>
                </Stack>
                {paymentRequired &&
                paymentAmount &&
                paymentCharges &&
                disabled &&
                formAuthType &&
                formAuthType !== FormAuthType.NIL ? (
                  <>
                    <Heading as="h4" size="md" pt={5}>
                      {locale === 'en'
                        ? 'Payment Details'
                        : locale === 'si'
                        ? 'ගෙවීම් තොරතුරු'
                        : 'கட்டண விவரங்கள்'}
                    </Heading>
                    {/* <SimpleGrid columns={{ sm: 1, md: 1 }} spacing="40px" pt={5}>
                {paymentCharges.map((data, index) => {
                  return (
                    <SimpleGrid columns={{ sm: 1, md: 2 }}>
                      <Stack>
                        <Text as="span" textStyle="subhead-1">
                          {index + 1}. Payment Charge name:
                        </Text>
                        <Text>{data.name}</Text>
                      </Stack>
                      <Stack>
                        <Text as="span" textStyle="subhead-1">
                          Payment Charge amount:
                        </Text>
                        <Text>{data.amount}</Text>
                      </Stack>
                    </SimpleGrid>
                  )
                })}
              </SimpleGrid> */}
                    <Stack
                      spacing={{ base: '0', md: '0.5rem' }}
                      direction={{ base: 'column', md: 'row' }}
                      pt={5}
                    >
                      <Text as="span" textStyle="subhead-1">
                        {locale === 'en'
                          ? 'Payment Amount Total :'
                          : locale === 'si'
                          ? 'මුළු ගෙවීම් මුදල :'
                          : 'கட்டணத் தொகை :'}
                      </Text>
                      <Text>{paymentAmount}</Text>
                    </Stack>
                  </>
                ) : null}
              </Box>
              {!preview ? (
                <>
                  {disabled ? (
                    <>
                      {signatureRequired ? (
                        <>
                          <Box height="70px" my={3} ml={10} mb={10}>
                            <FormControl
                              isInvalid={
                                mainFormValues?.formState?.errors?.type
                                  ? true
                                  : false
                              }
                            >
                              <FormLabel htmlFor="type" mb="8px">
                                {locale === 'en'
                                  ? 'Signature Type *'
                                  : locale === 'si'
                                  ? 'අත්සන වර්ගය *'
                                  : 'கையொப்ப வகை *'}
                              </FormLabel>
                              <Controller
                                name="type"
                                control={mainFormValues.control}
                                render={({ field }) => (
                                  <SingleSelect
                                    {...field}
                                    items={signatureTypesList}
                                    value={signatureType}
                                    placeholder={
                                      locale == 'en'
                                        ? 'Select an option'
                                        : locale == 'si'
                                        ? 'විකල්පයක් තෝරන්න'
                                        : 'ஒரு விருப்பத்தைத் தேர்ந்தெடுக்கவும்'
                                    }
                                    onChange={(value) => {
                                      setSignatureType(value)
                                      localStorage.setItem(
                                        'signatureType',
                                        value,
                                      )
                                      setSignatureName('')
                                      localStorage.removeItem('signature')
                                      mainFormValues.setValue('signature', '')
                                    }}
                                  />
                                )}
                                rules={{
                                  required: `${
                                    locale == 'en'
                                      ? 'Signature Type is required'
                                      : locale == 'si'
                                      ? 'අත්සන වර්ගය අවශ්‍යයි'
                                      : 'கையொப்ப வகை தேவை'
                                  }`,
                                }}
                              />
                              {mainFormValues.formState.errors.type && (
                                <FormErrorMessage>
                                  {mainFormValues.formState.errors.type.message}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          </Box>
                          {signatureType == 'FullName' && (
                            <Box height="70px" my={2} ml={10} mb={10}>
                              <FormControl
                                isInvalid={
                                  mainFormValues?.formState?.errors?.signature
                                    ? true
                                    : false
                                }
                                isRequired
                              >
                                <FormLabel htmlFor="Signature" mb="8px">
                                  {locale === 'en'
                                    ? 'Signature'
                                    : locale === 'si'
                                    ? 'අත්සන'
                                    : 'கையெழுத்து'}
                                </FormLabel>
                                <Input
                                  isRequired={true}
                                  placeholder={
                                    locale === 'en'
                                      ? 'Signature'
                                      : locale === 'si'
                                      ? 'අත්සන'
                                      : 'கையெழுத்து'
                                  }
                                  id="signature"
                                  maxLength={255}
                                  {...mainFormValues.register('signature', {
                                    required: `${
                                      locale == 'en'
                                        ? 'Signature is required'
                                        : locale == 'si'
                                        ? 'අත්සන අවශ්‍යයි'
                                        : 'கையொப்பம் தேவை'
                                    }`,
                                    maxLength: {
                                      value: 255,
                                      message: 'Max length is 255',
                                    },
                                  })}
                                  onChange={(e) => {
                                    setSignatureName(e.target.value)
                                    localStorage.setItem(
                                      'signatureType',
                                      signatureType,
                                    )
                                  }}
                                />

                                {mainFormValues.formState.errors.signature && (
                                  <FormErrorMessage>
                                    {
                                      mainFormValues.formState.errors.signature
                                        .message
                                    }
                                  </FormErrorMessage>
                                )}
                              </FormControl>
                            </Box>
                          )}
                          {signatureType == 'Touchpad' ? (
                            <Box height="110px" my={2} ml={10} mb={10}>
                              <FormControl
                                isInvalid={
                                  mainFormValues?.formState?.errors?.signature
                                    ? true
                                    : false
                                }
                                isRequired
                              >
                                <FormLabel htmlFor="Signature" mb="8px">
                                  {locale === 'en'
                                    ? 'Signature'
                                    : locale === 'si'
                                    ? 'අත්සන'
                                    : 'கையெழுத்து'}
                                </FormLabel>
                                <Box
                                  borderRadius="5"
                                  border="1px"
                                  borderColor="black.200"
                                  w="300px"
                                >
                                  <SignatureCanvas
                                    penColor="black"
                                    canvasProps={{
                                      width: 300,
                                      height: 80,
                                      className: 'sigCanvas',
                                    }}
                                    ref={signPad}
                                    onEnd={handleOnEnd}
                                  />
                                </Box>
                                {mainFormValues.formState.errors.signature && (
                                  <FormErrorMessage>
                                    {
                                      mainFormValues.formState.errors.signature
                                        .message
                                    }
                                  </FormErrorMessage>
                                )}
                              </FormControl>
                            </Box>
                          ) : signatureType == 'Image' ? (
                            <>
                              <Box my={8} ml={10} mb={10}>
                                <FormControl>
                                  <FormLabel htmlFor="Signature" mb="8px">
                                    {locale === 'en'
                                      ? 'Signature'
                                      : locale === 'si'
                                      ? 'අත්සන'
                                      : 'கையெழுத்து'}
                                  </FormLabel>
                                  <Stack direction="row" spacing="0.5rem">
                                    <Center
                                      bg={'white'}
                                      h="154px"
                                      w="424px"
                                      borderWidth="1px"
                                      borderColor="gray.400"
                                      borderStyle="dashed"
                                      borderRadius="12px"
                                    >
                                      <Image
                                        h="150px"
                                        w="420px"
                                        // m={'2px'}
                                        src={signatureImage}
                                        objectFit="contain"
                                        fallbackSrc={DefaultUploadImage}
                                      />
                                    </Center>
                                    <Stack direction="row" spacing="0.5rem">
                                      <IconButton
                                        // isDisabled={isLoading}
                                        variant="outline"
                                        aria-label="Agency logo upload"
                                        icon={<BiUpload />}
                                        // mb={5}
                                        ml={15}
                                        onClick={() =>
                                          signatureImageRef.current?.click()
                                        }
                                      />
                                      <IconButton
                                        // isDisabled={isLoading}
                                        variant="outline"
                                        aria-label="Agency logo upload"
                                        icon={<BiMessageSquareX />}
                                        // mb={5}
                                        disabled={signatureImage === ''}
                                        onClick={removeImage}
                                      />
                                    </Stack>
                                  </Stack>
                                  <Input
                                    name="agencyLogo"
                                    type="file"
                                    ref={signatureImageRef}
                                    accept="image/png, image/jpg, image/jpeg"
                                    onChange={async (e) => {
                                      if (!e.target.files) return
                                      const validAgencyLogo =
                                        await validateSignatureImage(
                                          e?.target?.files[0],
                                        )
                                      if (!validAgencyLogo) {
                                        return
                                      }
                                      setSignatureImageFile(e?.target?.files[0])
                                      if (signatureImageRef.current) {
                                        signatureImageRef.current.value = ''
                                      }
                                    }}
                                    display="none"
                                  />
                                  {mainFormValues.formState.errors
                                    .signature && (
                                    <div
                                      style={{
                                        color: '#bf3d3d',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '0.85rem',
                                      }}
                                    >
                                      <Icon
                                        h="1.5rem"
                                        as={BxsErrorCircle}
                                        style={{ marginRight: '0.5rem' }}
                                      />
                                      <Box my="0.125rem">
                                        {
                                          mainFormValues.formState.errors
                                            .signature?.message
                                        }
                                      </Box>
                                    </div>
                                  )}
                                </FormControl>
                              </Box>
                            </>
                          ) : null}
                          <Box height="70px" my={2} ml={10}>
                            <FormControl
                              isInvalid={
                                mainFormValues?.formState?.errors?.date
                                  ? true
                                  : false
                              }
                            >
                              <FormLabel htmlFor="date" mb="8px">
                                {locale === 'en'
                                  ? 'Date'
                                  : locale === 'si'
                                  ? 'දිනය'
                                  : 'தேதி'}
                              </FormLabel>
                              <Input
                                placeholder={'Date'}
                                id="date"
                                maxLength={100}
                                disabled={true}
                                {...mainFormValues.register('date', {
                                  required: `${
                                    locale == 'en'
                                      ? 'Signature Date is required'
                                      : locale == 'si'
                                      ? 'අත්සන් දිනය අවශ්ය වේ'
                                      : 'கையொப்பம் தேதி தேவை'
                                  }`,
                                })}
                              />
                              {mainFormValues.formState.errors.date && (
                                <FormErrorMessage>
                                  {mainFormValues.formState.errors.date.message}
                                </FormErrorMessage>
                              )}
                            </FormControl>
                          </Box>
                        </>
                      ) : null}
                      <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacing="40px"
                        pt={5}
                      >
                        <PublicFormSubmitButton
                          formFields={augmentedFormFields}
                          formLogics={formLogics}
                          colorTheme={colorTheme}
                        />
                        <Stack
                          px={{ base: '1rem', md: 0 }}
                          pt="2.5rem"
                          pb="4rem"
                        >
                          <Button
                            onClick={() => {
                              setDisabled(false)
                            }}
                          >
                            {locale === 'en'
                              ? 'Cancel'
                              : locale === 'si'
                              ? 'අවලංගු කරන්න'
                              : 'ரத்து செய்'}
                          </Button>
                        </Stack>
                      </SimpleGrid>
                    </>
                  ) : (
                    <SimpleGrid
                      columns={{ sm: 1, md: 2 }}
                      spacing="40px"
                      pt={5}
                    >
                      <Stack px={{ base: '1rem', md: 0 }} pt="2.5rem" pb="4rem">
                        <Button
                          w="100%"
                          colorScheme={
                            `theme-${colorTheme}` as ThemeColorScheme
                          }
                          onClick={() => {
                            setDisabled(true)
                          }}
                        >
                          {locale === 'en'
                            ? 'Preview'
                            : locale === 'si'
                            ? 'පෙරදසුන'
                            : 'முன்னோட்ட'}
                        </Button>
                      </Stack>
                      <Stack px={{ base: '1rem', md: 0 }} pt="2.5rem" pb="4rem">
                        <Button
                          onClick={() => {
                            // setDraftId(null)
                            setCancelModal(true)
                            // navigate('/citizen/dashboard')
                          }}
                        >
                          {locale === 'en'
                            ? 'Cancel'
                            : locale === 'si'
                            ? 'අවලංගු කරන්න'
                            : 'ரத்து செய்'}
                        </Button>
                      </Stack>
                      <Stack
                        px={{ base: '1rem', md: 0 }}
                        pt="2.5rem"
                        pb="4rem"
                        hidden={true}
                      >
                        <Button
                          ref={draftButtonRef}
                          onClick={saveSubmissionAsDraft}
                        >
                          {locale === 'en'
                            ? 'Save as draft'
                            : locale === 'si'
                            ? 'කෙටුම්පතක් ලෙස සුරකින්න'
                            : 'வரைவாக சேமி'}
                        </Button>
                      </Stack>
                    </SimpleGrid>
                  )}
                </>
              ) : null}
            </div>
          )}
        </form>
      </FormProvider>
    </IntlProvider>
  )
}
